/* eslint-disable react-hooks/exhaustive-deps */
/*
 * Config sub-tab — toggles + intervals for the autoban background task.
 * Patches are partial; only changed fields are sent to the API.
 */
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Settings, Save, Send, ShieldAlert, RotateCcw } from "lucide-react";

export default function ConfigTab({ toast }) {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ruleServer, setRuleServer] = useState("");
  const [servers, setServers] = useState([]);
  const [busyKey, setBusyKey] = useState(null);

  const loadRules = async (server) => {
    try {
      const { data } = await api.get("/admin/moderation/cheat-rules", {
        params: server ? { server } : {},
      });
      setRules(data.rules || []);
      setCategories(data.categories || []);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    api.get("/admin/moderation/config").then(({ data }) => { setCfg(data); setDraft(data); });
    api.get("/admin/moderation/servers").then(({ data }) => setServers(data.servers || []));
  }, []);

  useEffect(() => { loadRules(ruleServer); }, [ruleServer]);

  const toggleRule = async (rule, enabled) => {
    setBusyKey(rule.key);
    try {
      await api.patch(`/admin/moderation/cheat-rules/${rule.key}`, {
        enabled,
        ...(ruleServer ? { server: ruleServer } : {}),
      });
      toast.success(`${rule.label} — ${enabled ? "ON" : "OFF"}${ruleServer ? ` on ${ruleServer}` : " (all servers)"}`);
      await loadRules(ruleServer);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  const resetRule = async (rule) => {
    setBusyKey(rule.key);
    try {
      await api.delete(`/admin/moderation/cheat-rules/${rule.key}`, {
        params: ruleServer ? { server: ruleServer } : {},
      });
      toast.success(`${rule.label} — override cleared, back to the default`);
      await loadRules(ruleServer);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  const save = async () => {
    const patch = {};
    Object.keys(draft).forEach((k) => {
      if (draft[k] !== cfg[k]) patch[k] = draft[k];
    });
    if (Object.keys(patch).length === 0) { toast.success("No changes"); return; }
    try {
      const { data } = await api.patch("/admin/moderation/config", patch);
      setCfg(data); setDraft(data);
      toast.success("Config saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  const testWebhook = async () => {
    if (!draft.log_channel_url) { toast.error("Save a webhook URL first"); return; }
    try {
      const { data } = await api.post("/admin/moderation/config/test-webhook");
      if (data.ok) toast.success("Webhook posted to Discord — check the channel");
      else toast.error("Discord rejected the webhook. Double-check the URL.");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!cfg) return <div className="glass-card p-6 text-[var(--muted)] text-sm">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="glass-card p-4 space-y-4" data-testid="config-form">
        <h3 className="label flex items-center gap-2"><Settings size={14} /> Auto-Ban Configuration</h3>

        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={!!draft.auto_ban_enabled} onChange={(e) => setDraft({ ...draft, auto_ban_enabled: e.target.checked })} data-testid="config-autoban" />
          <span>
            <strong>Website auto-ban loop</strong> — off by default. The Discord bot already auto-bans
            (and can ban the Discord account, which the website can&apos;t). Turning this on means two bans
            and two notifications per detection.
          </span>
        </label>

        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={!!draft.banlist_sync_enabled} onChange={(e) => setDraft({ ...draft, banlist_sync_enabled: e.target.checked })} data-testid="config-banlistsync" />
          <span><strong>Banlist.txt sync</strong> — write every ban to Pal&apos;s native banlist.txt over SFTP</span>
        </label>

        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--muted)] block mb-1">Poll interval (seconds)</label>
          <input
            type="number"
            min="5"
            max="600"
            value={draft.poll_interval || ""}
            onChange={(e) => setDraft({ ...draft, poll_interval: parseInt(e.target.value, 10) || null })}
            className="input"
            data-testid="config-poll"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--muted)] block mb-1">Auto-unban check interval (seconds)</label>
          <input
            type="number"
            min="15"
            max="3600"
            value={draft.auto_unban_interval || ""}
            onChange={(e) => setDraft({ ...draft, auto_unban_interval: parseInt(e.target.value, 10) || null })}
            className="input"
            data-testid="config-unban-interval"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--muted)] block mb-1">Discord log webhook URL (optional)</label>
          <input
            type="url"
            value={draft.log_channel_url || ""}
            onChange={(e) => setDraft({ ...draft, log_channel_url: e.target.value })}
            className="input"
            placeholder="https://discord.com/api/webhooks/…"
            data-testid="config-log-url"
          />
          <p className="text-[10px] text-[var(--muted)] mt-1">
            Used to mirror every ban / unban / appeal event into your moderation Discord channel.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={save} className="btn btn-teal" data-testid="config-save"><Save size={14} /> Save Config</button>
          <button onClick={testWebhook} className="btn btn-ghost" data-testid="config-test-webhook"><Send size={14} /> Test Webhook</button>
        </div>
      </div>

      <div className="glass-card p-4" data-testid="cheat-rules-panel">
        <h3 className="label flex items-center gap-2 mb-1">
          <ShieldAlert size={14} /> Detection switchboard ({rules.length})
        </h3>
        <p className="text-xs text-[var(--muted)] mb-1">
          These switches control <strong>the Discord bot&apos;s</strong> auto-ban. Changes save instantly
          and take effect <strong>within ~3 minutes</strong> — the bot re-reads them at the top of every
          scan, no restart needed.
        </p>
        <p className="text-[11px] text-[var(--muted)] mb-3">
          PalDefender emits one undifferentiated &quot;<em>may be</em> a cheater&quot; stream and its rules
          are tuned for vanilla Palworld. The pal detections are off because the Pal Builder legitimately
          sells past every vanilla ceiling on modded servers.
        </p>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-xs uppercase tracking-wider text-[var(--muted)]">Scope</span>
          <select
            value={ruleServer}
            onChange={(e) => setRuleServer(e.target.value)}
            className="input max-w-xs"
            data-testid="cheat-rules-scope"
          >
            <option value="">All servers (*)</option>
            {servers.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
          </select>
        </div>

        <div className="space-y-5">
          {categories.map((cat) => {
            const inCat = rules.filter((r) => r.category === cat.id);
            if (inCat.length === 0) return null;
            return (
              <div key={cat.id} data-testid={`cheat-rule-group-${cat.id}`}>
                <h4 className="text-xs uppercase tracking-[0.14em] text-[var(--teal)] mb-2">{cat.label}</h4>
                <div className="space-y-2">
                  {inCat.map((r) => (
                    <div
                      key={r.key}
                      className="flex items-start gap-3 px-3 py-2.5 rounded bg-black/20 border border-white/5 transition-colors hover:border-white/10"
                    >
                      <input
                        type="checkbox"
                        checked={!!r.enabled}
                        disabled={busyKey === r.key}
                        onChange={(e) => toggleRule(r, e.target.checked)}
                        className="mt-0.5"
                        data-testid={`cheat-rule-${r.key}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm text-[var(--bone)]">{r.label}</span>
                          <span className="text-[10px] font-mono text-[var(--muted)]">{r.key}</span>
                          {r.overridden ? (
                            <span
                              className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-teal-500/20 text-teal-300"
                              data-testid={`cheat-rule-badge-${r.key}`}
                            >
                              Override
                            </span>
                          ) : (
                            <span
                              className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/10 text-[var(--muted)]"
                              data-testid={`cheat-rule-badge-${r.key}`}
                            >
                              Default {r.default_enabled ? "on" : "off"}
                            </span>
                          )}
                          {r.observed > 0 ? (
                            <span className="text-[10px] text-amber-300/80">{r.observed} seen in logs</span>
                          ) : null}
                        </div>
                        <p className="text-[11px] text-[var(--muted)] mt-0.5">{r.note}</p>
                      </div>
                      {r.overridden ? (
                        <button
                          onClick={() => resetRule(r)}
                          disabled={busyKey === r.key}
                          className="btn btn-ghost px-2"
                          title="Clear this override and fall back to the default"
                          data-testid={`cheat-rule-reset-${r.key}`}
                        >
                          <RotateCcw size={13} />
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
